import type { ApolloError, FetchResult } from '@apollo/client';
import { Button, Divider, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import React, { useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import Alert from '@components/Alert/Alert';
import { FormikTextField } from '@components/FormikFields';
import FormikCheckboxField from '@components/FormikFields/FormikCheckboxField';
import Icon from '@components/Icon/Icon';
import IconButton from '@components/IconButton';

import {
  APP_VERSION_APP_LABEL_MESSAGES,
  APP_VERSION_PLATFORM_ICONS,
  APP_VERSION_PLATFORM_LABEL_MESSAGES,
} from '@constants/app-version';

import {
  useCreateAppVersionDraft,
  usePublishAppVersion,
  useUpdateAppVersionDraft,
} from '@hooks/api/app-version';
import { useResponseHandler } from '@hooks/useResponseHandler';

import {
  isStrictSemver,
  isVersionHigherThanPublished,
} from '@utils/app-version';

import type {
  AdminCreateAppVersionDraftMutation,
  AppVersionSummaryFieldsFragment,
} from 'generated/app-version.generated';
import { AppVersionStatus } from 'generated/types';

import PublishConfirmationDialog from './PublishConfirmationDialog';
import ReleaseDateField from './ReleaseDateField';
import {
  parseReleaseDate,
  startOfToday,
  toReleaseDateInput,
} from './release-date';

const messages = defineMessages({
  versionNumber: {
    id: 'admin.configurations.versionNumber',
    defaultMessage: 'Version number',
  },
  releaseNotes: {
    id: 'admin.configurations.releaseNotes',
    defaultMessage: 'Release notes',
  },
  releaseDate: {
    id: 'admin.configurations.releaseDate',
    defaultMessage: 'Release date',
  },
  minimumVersion: {
    id: 'admin.configurations.minimumVersion',
    defaultMessage: 'Set as minimum version',
  },
  saveDraft: {
    id: 'admin.configurations.saveDraft',
    defaultMessage: 'Save draft',
  },
  publish: {
    id: 'admin.configurations.publish',
    defaultMessage: 'Publish',
  },
  publishWarning: {
    id: 'admin.configurations.publishWarning',
    defaultMessage: 'Changes only apply upon publishing',
  },
  closeForm: {
    id: 'admin.configurations.closeForm',
    defaultMessage: 'Close form',
  },
  newReleaseTitle: {
    id: 'admin.configurations.newReleaseTitle',
    defaultMessage: 'New Release - {platform}',
  },
  editReleaseTitle: {
    id: 'admin.configurations.editReleaseTitle',
    defaultMessage: 'Edit Release - {platform}',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  invalidSemver: {
    id: 'admin.configurations.invalidSemver',
    defaultMessage: 'Enter a valid semver version (for example, 1.2.3)',
  },
  versionTooLow: {
    id: 'admin.configurations.versionTooLow',
    defaultMessage:
      'Version must be higher than the current published version ({version})',
  },
  releaseNotesRequired: {
    id: 'admin.configurations.releaseNotesRequired',
    defaultMessage: 'Release notes are required to publish',
  },
  releaseDateRequired: {
    id: 'admin.configurations.releaseDateRequired',
    defaultMessage: 'Release date is required to publish',
  },
  releaseDateFutureNotAllowed: {
    id: 'admin.configurations.releaseDateFutureNotAllowed',
    defaultMessage: 'Release date cannot be in the future',
  },
  saveDraftError: {
    id: 'admin.configurations.saveDraftError',
    defaultMessage: 'Could not save draft. Please try again.',
  },
  publishError: {
    id: 'admin.configurations.publishError',
    defaultMessage: 'Could not publish release. Please try again.',
  },
});

export type ReleaseFormMode = 'create' | 'edit';

export interface ReleaseFormValues {
  version_number: string;
  release_notes: string;
  release_date: Date | null;
  is_minimum_version: boolean;
}

type SubmitAction = 'draft' | 'publish';

type SaveDraftResult = {
  draftId: string | null;
  success: boolean;
  errorMessage?: string;
};

function getFirstGraphqlErrorMessage(
  errors: ReadonlyArray<{ message?: string }> | undefined
): string | null {
  const message = errors?.[0]?.message;
  return typeof message === 'string' && message.length > 0 ? message : null;
}

interface ReleaseFormProps {
  summary: AppVersionSummaryFieldsFragment;
  mode: ReleaseFormMode;
  onClose: () => void;
}

function buildInitialValues(
  summary: AppVersionSummaryFieldsFragment,
  mode: ReleaseFormMode
): ReleaseFormValues {
  if (mode === 'edit') {
    return {
      version_number: summary.draft_version ?? '',
      release_notes: summary.draft_release_notes ?? '',
      release_date: parseReleaseDate(summary.draft_release_date),
      is_minimum_version: summary.draft_is_minimum_version ?? false,
    };
  }

  return {
    version_number: '',
    release_notes: '',
    release_date: startOfToday(),
    is_minimum_version: false,
  };
}

function ReleaseForm({ summary, mode, onClose }: ReleaseFormProps) {
  const { formatMessage } = useIntl();
  const { palette } = useTheme();
  const { parseError } = useResponseHandler();
  const [createDraft, { loading: creating }] = useCreateAppVersionDraft();
  const [updateDraft, { loading: updating }] = useUpdateAppVersionDraft();
  const [publishVersion, { loading: publishing }] = usePublishAppVersion();
  const submitActionRef = useRef<SubmitAction>('draft');
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);
  const [workingDraftId, setWorkingDraftId] = useState<string | null>(
    summary.draft_id ?? null
  );

  const loading = creating || updating || publishing;
  const platformLabel = formatMessage(
    APP_VERSION_PLATFORM_LABEL_MESSAGES[summary.platform]
  );
  const appLabel = formatMessage(APP_VERSION_APP_LABEL_MESSAGES[summary.app]);
  const platformIcon = APP_VERSION_PLATFORM_ICONS[summary.platform];
  const formTitle = formatMessage(
    mode === 'edit' ? messages.editReleaseTitle : messages.newReleaseTitle,
    { platform: platformLabel }
  );

  const validationSchema = Yup.object({
    version_number: Yup.string()
      .trim()
      .required(formatMessage(messages.required))
      .test('semver', formatMessage(messages.invalidSemver), (value) =>
        isStrictSemver(value)
      )
      .test(
        'higher-than-published',
        formatMessage(messages.versionTooLow, {
          version: summary.latest_published_version ?? '',
        }),
        (value) =>
          isVersionHigherThanPublished(value, summary.latest_published_version)
      ),
    release_notes: Yup.string().nullable(),
    release_date: Yup.date()
      .nullable()
      .max(startOfToday(), formatMessage(messages.releaseDateFutureNotAllowed)),
    is_minimum_version: Yup.boolean().required(),
  });

  const historyInput = {
    app: summary.app,
    platform: summary.platform,
    status: AppVersionStatus.published,
  };

  const getMutationErrorMessage = (
    result: FetchResult<unknown>,
    fallbackMessage: string
  ) => {
    if (!result.errors?.length) {
      return null;
    }

    return (
      getFirstGraphqlErrorMessage(result.errors) ??
      parseError(result.errors)?.message ??
      fallbackMessage
    );
  };

  const getThrownErrorMessage = (
    error: unknown,
    fallbackMessage: string
  ): string => {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    const parsed = parseError(error as ApolloError);
    return parsed?.message ?? fallbackMessage;
  };

  const saveDraft = async (
    values: ReleaseFormValues
  ): Promise<SaveDraftResult> => {
    const payload = {
      version_number: values.version_number.trim(),
      release_notes: values.release_notes,
      release_date: toReleaseDateInput(values.release_date),
      is_minimum_version: values.is_minimum_version,
    };
    const effectiveDraftId = workingDraftId || summary.draft_id;

    try {
      if (effectiveDraftId) {
        const result = await updateDraft({
          app_version_id: effectiveDraftId,
          ...payload,
        });

        const errorMessage = getMutationErrorMessage(
          result,
          formatMessage(messages.saveDraftError)
        );
        if (errorMessage) {
          return { success: false, draftId: null, errorMessage };
        }

        return { success: true, draftId: effectiveDraftId };
      }

      const result = await createDraft({
        app: summary.app,
        platform: summary.platform,
        ...payload,
      });

      const errorMessage = getMutationErrorMessage(
        result,
        formatMessage(messages.saveDraftError)
      );
      if (errorMessage) {
        return { success: false, draftId: null, errorMessage };
      }

      const data = result.data as
        AdminCreateAppVersionDraftMutation | undefined;
      const draftId = data?.createAppVersionDraft.app_version_id ?? null;
      if (draftId) {
        setWorkingDraftId(draftId);
      }

      return {
        success: true,
        draftId,
      };
    } catch (error) {
      return {
        success: false,
        draftId: null,
        errorMessage: getThrownErrorMessage(
          error,
          formatMessage(messages.saveDraftError)
        ),
      };
    }
  };

  const validatePublishRequirements = (
    values: ReleaseFormValues,
    setFieldError: FormikHelpers<ReleaseFormValues>['setFieldError']
  ): boolean => {
    let isValid = true;

    if (!values.release_notes.trim()) {
      setFieldError(
        'release_notes',
        formatMessage(messages.releaseNotesRequired)
      );
      isValid = false;
    }

    if (!values.release_date) {
      setFieldError(
        'release_date',
        formatMessage(messages.releaseDateRequired)
      );
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (
    values: ReleaseFormValues,
    { setSubmitting, setFieldError }: FormikHelpers<ReleaseFormValues>
  ) => {
    setFormError(null);

    try {
      if (submitActionRef.current === 'publish') {
        if (!validatePublishRequirements(values, setFieldError)) {
          return;
        }

        const { success, draftId, errorMessage } = await saveDraft(values);
        if (!success || !draftId) {
          setFormError(errorMessage ?? formatMessage(messages.saveDraftError));
          return;
        }

        const publishResult = await publishVersion(
          { app_version_id: draftId },
          historyInput
        );
        const publishErrorMessage = getMutationErrorMessage(
          publishResult,
          formatMessage(messages.publishError)
        );
        if (publishErrorMessage) {
          setFormError(publishErrorMessage);
          return;
        }
      } else {
        const { success, errorMessage } = await saveDraft(values);
        if (!success) {
          setFormError(errorMessage ?? formatMessage(messages.saveDraftError));
          return;
        }
      }

      onClose();
    } catch (error) {
      setFormError(
        getThrownErrorMessage(
          error,
          submitActionRef.current === 'publish'
            ? formatMessage(messages.publishError)
            : formatMessage(messages.saveDraftError)
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper
      data-testid="release-form-container"
      elevation={0}
      sx={{
        border: `2px solid ${palette.primary.main}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Stack
        alignItems="center"
        bgcolor="common.accentLightblue"
        borderBottom={`1px solid ${palette.divider}`}
        direction="row"
        justifyContent="space-between"
        px={2}
        py={1.25}
      >
        <Stack alignItems="center" direction="row" spacing={1}>
          <Icon color={palette.primary.main} name={platformIcon} type="fab" />
          <Typography color="primary" fontWeight={700} variant="subtitle1">
            {formTitle}
          </Typography>
        </Stack>
        <Stack alignItems="center" direction="row" spacing={1}>
          <Divider flexItem orientation="vertical" />
          <IconButton
            color={palette.text.secondary}
            iconType="fas"
            name="xmark"
            tooltip={formatMessage(messages.closeForm)}
            onClick={onClose}
          />
        </Stack>
      </Stack>

      <Formik
        enableReinitialize
        initialValues={buildInitialValues(summary, mode)}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          isSubmitting,
          submitForm,
          validateForm,
          setFieldError,
          setTouched,
          values,
        }) => (
          <>
            <Form data-testid="release-form">
              <Stack spacing={2} p={2.5}>
                {formError && (
                  <Alert data-testid="release-form-error">{formError}</Alert>
                )}

                <Stack
                  alignItems={{ xs: 'stretch', sm: 'flex-end' }}
                  direction={{ xs: 'column', sm: 'row' }}
                  gap={2}
                >
                  <Stack flex={1} minWidth={0}>
                    <FormikTextField
                      name="version_number"
                      label={formatMessage(messages.versionNumber)}
                      required
                    />
                  </Stack>
                  <FormikCheckboxField
                    name="is_minimum_version"
                    label={formatMessage(messages.minimumVersion)}
                  />
                </Stack>

                <ReleaseDateField
                  label={formatMessage(messages.releaseDate)}
                  maxDate={startOfToday()}
                  required
                />
                <FormikTextField
                  name="release_notes"
                  label={formatMessage(messages.releaseNotes)}
                  type="text-area"
                />

                <Stack
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  direction={{ xs: 'column', sm: 'row' }}
                  gap={2}
                  justifyContent="space-between"
                  pt={0.5}
                >
                  <Stack alignItems="center" direction="row" spacing={1}>
                    <Icon
                      color={palette.warning.main}
                      name="triangle-exclamation"
                      type="fas"
                    />
                    <Typography color="warning.dark" variant="body2">
                      {formatMessage(messages.publishWarning)}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={1}
                    justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
                  >
                    <Button
                      color="primary"
                      disabled={isSubmitting || loading}
                      startIcon={<Icon name="floppy-disk" type="fas" />}
                      sx={{ borderRadius: '29px', textTransform: 'none' }}
                      type="button"
                      variant="outlined"
                      onClick={() => {
                        submitActionRef.current = 'draft';
                        void submitForm();
                      }}
                    >
                      {formatMessage(messages.saveDraft)}
                    </Button>
                    <Button
                      color="success"
                      data-testid="release-form-publish-button"
                      disabled={isSubmitting || loading}
                      startIcon={<Icon name="paper-plane" type="fas" />}
                      sx={{ borderRadius: '29px', textTransform: 'none' }}
                      type="button"
                      variant="contained"
                      onClick={() => {
                        void (async () => {
                          setFormError(null);
                          const errors = await validateForm();
                          await setTouched(
                            {
                              version_number: true,
                              release_notes: true,
                              release_date: true,
                              is_minimum_version: true,
                            },
                            false
                          );

                          if (Object.keys(errors).length > 0) {
                            return;
                          }

                          if (
                            !validatePublishRequirements(values, setFieldError)
                          ) {
                            return;
                          }

                          setConfirmPublishOpen(true);
                        })();
                      }}
                    >
                      {formatMessage(messages.publish)}
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </Form>

            <PublishConfirmationDialog
              appLabel={appLabel}
              confirming={isSubmitting || loading}
              isMinimumVersion={values.is_minimum_version}
              open={confirmPublishOpen}
              platformLabel={platformLabel}
              releaseNotes={values.release_notes}
              versionNumber={values.version_number.trim()}
              onCancel={() => {
                if (isSubmitting || loading) {
                  return;
                }
                setConfirmPublishOpen(false);
              }}
              onConfirm={() => {
                if (isSubmitting || loading) {
                  return;
                }
                setConfirmPublishOpen(false);
                submitActionRef.current = 'publish';
                void submitForm();
              }}
            />
          </>
        )}
      </Formik>
    </Paper>
  );
}

export default ReleaseForm;
